<template>
	<div class="notice-list-container">
		<div class="top-bar-container">
			<DcHeader :title="title" />
			<div class="dropdown-container" v-if="pageType === 'quantity'">
				<van-dropdown-menu active-color="#455bd9">
					<van-dropdown-item v-model="currentTab" :options="tabOptions" @change="onDropdownChange">
						<template #title>
							<span class="dropdown-title">{{ tabList[currentTab]?.name }}</span>
						</template>
					</van-dropdown-item>
				</van-dropdown-menu>
			</div>
		</div>

		<!-- 列表内容 -->
		<PaginationList
			ref="paginationListRef"
			:fetch-data="fetchNoticeList"
			:initial-list="ssrListRes?.data?.list ?? []"
			:scroll-container-style="{ height: 'calc(100vh - 60px)' }"
			:immediate="true"
			class="notice-list"
		>
			<template #list="{ list }">
				<component
					:is="item.linkUrl ? 'a' : NuxtLink"
					class="info-box"
					v-for="(item, i) in list"
					:key="i + (item.title || '')"
					v-bind="getItemLinkAttrs(item)"
				>
					<!-- 国械头条样式 -->
					<template v-if="pageType === 'hot'">
						<div class="hot-info">
							<div class="hot-content">
								<div class="hot-title">{{ item.title }}</div>
								<div class="data-info">{{ getDisplayTime(item) }}</div>
							</div>
							<!-- 无论是否配置封面都占位：没有封面时用头条默认图 -->
							<div class="right-img" v-if="!isCoverHidden(item)">
								<img
									:src="getCoverSrc(item)"
									:alt="item.title"
									class="cover-img"
									loading="lazy"
									@error="handleImageError($event, item)"
								/>
							</div>
						</div>
					</template>

					<!-- 质量公告样式 -->
					<template v-else>
						<div class="quantity-info">
							<div class="title-wrap">
								<div class="quantity-title">{{ item.title }}</div>
							</div>
							<div class="data-info">{{ getDisplayTime(item) }}</div>
						</div>
					</template>
				</component>
			</template>
		</PaginationList>
	</div>
</template>

<script setup>
import { ref, computed, nextTick, reactive } from 'vue';
import { useRoute } from 'vue-router';
import { useHead, useRuntimeConfig, useSeoMeta } from '#imports';
import DcHeader from '@/components/DcHeader.vue';
import { NuxtLink } from '#components';
import PaginationList from '@/components/core/PaginationList.vue';
import { buildNoticeListParams, getIndexAnnouncement } from '@/api/notice.js';
import { isExternalLink } from '#shared/externalLink';
import { buildNoticeItemHref } from '@/utils/link/noticeDetail';

const route = useRoute();

/** 登录态用 cookie 判定：SSR 与客户端首屏一致 */
const tokenCookie = useCookie('cmdc_access_token_mobile');
const isLoggedIn = computed(() => !!tokenCookie.value);

// 封面兜底图（必须使用 public 下真实存在的资源，否则 error 会循环触发导致闪烁）
const COVER_PLACEHOLDER = '/staticSeo/images/coverImg-toutiao.png';
/** coverUrl -> 'fallback'（已换占位图）| 'hidden'（占位图也失败，直接不渲染） */
const coverStatus = reactive(new Map());

const props = defineProps({
	/** hot=头条（/topstory） / quantity=质量公告（/announcement） */
	pageType: {
		type: String,
		default: 'hot',
	},
});

// 由页面传入，避免路由切换瞬间 params 为空导致标题/样式闪成 hot
const pageType = computed(() => (props.pageType === 'quantity' ? 'quantity' : 'hot'));
const isGxzz = computed(() => String(route.query.isGxzz || '').trim() === '1' || route.path.startsWith('/gxzz/'));

const titleMap = {
	hot: '国械头条',
	quantity: '质量公告',
};
const title = computed(() => titleMap[pageType.value] || '资讯列表');

const pageSize = 10;
const pageStart = computed(() => parseInt(route.query.page) || 1);

// SEO：分页标题带页码；canonical 指向「无参数的标准 URL」
const config = useRuntimeConfig();
const pcBase = (config.public.publicPcUrl || '').replace(/\/$/, '');
const SHARE_IMAGE = 'https://uat-cmdc.oss-cn-beijing.aliyuncs.com/productFile/2022/08/18/9af2756f-27bf-4d1e-b0d1-8397e82bb2a0.png';
const isQuantity = computed(() => pageType.value === 'quantity');

// 国械智造头条（/gxzz/topstory）专用 TDK；仅国械智造头条生效，质量公告不受影响
const isGxzzHeadline = computed(() => isGxzz.value && !isQuantity.value);

const pageTitle = computed(() => {
	if (isGxzzHeadline.value) {
		const base = '国械智造头条 - 国械智造';
		return pageStart.value > 1 ? `国械智造头条 - 第${pageStart.value}页 - 国械智造` : base;
	}
	const name = isQuantity.value ? '质量公告' : '国械在线头条';
	return pageStart.value > 1 ? `${name} - 第${pageStart.value}页 - 国械在线` : `${name} - 国械在线`;
});
const pageDescription = computed(() => {
	if (isGxzzHeadline.value)
		return '国械智造头条汇集国药影像（国药通用（深圳）医疗影像有限公司）、国药创研（国药集团医疗器械研究院有限公司）、国药新光（国药新光医疗科技有限公司）3大自有品牌企业产品动态。';
	if (isQuantity.value)
		return '国械在线“质量公告”频道提供医疗器械产品注册证吊销、召回、抽检公告，医疗器械暂停/恢复进口、经营、使用以及处罚公告和监督检查等内容。';
	return '国械在线头条频道提供国械在线平台规则、政策、活动、功能迭代等相关内容。';
});
const pageKeywords = computed(() => {
	if (isGxzzHeadline.value) return '国械智造头条,国械智造,国药影像,国药创研,国药新光';
	if (isQuantity.value) return '质量公告,国械在线';
	return '国械在线头条,国械在线';
});

useSeoMeta({
	title: pageTitle,
	ogTitle: pageTitle,
	description: pageDescription,
	ogDescription: pageDescription,
	keywords: pageKeywords,
	ogImage: SHARE_IMAGE,
});

// 带参数（?page=2、?isGxzz=1 等）的页面，canonical 一律指向 PC 端无参数的标准 URL；国械智造加 /gxzz 前缀
const canonicalHref = computed(() => {
	const menu = isQuantity.value ? '/announcement' : '/topstory';
	const path = isGxzz.value ? `/gxzz${menu}` : menu;
	return `${pcBase}${path}`;
});
useHead(() => ({
	meta: [
		{ name: 'applicable-device', content: 'mobile' },
		{ 'http-equiv': 'Cache-Control', content: 'no-siteapp' },
		{ 'http-equiv': 'Cache-Control', content: 'no-transform' },
	],
	link: [{ rel: 'canonical', href: canonicalHref.value, key: 'canonical' }],
}));

// Tab 相关（仅质量公告）
const currentTab = ref(0);
const tabList = [
	{ name: '医疗器械产品注册证吊销公告', type: 1 },
	{ name: '医疗器械产品召回公告', type: 2 },
	{ name: '医疗器械产品抽检公告', type: 3 },
	{ name: '医疗器械暂停/恢复进口、经营和使用公告', type: 5 },
	{ name: '处罚公告', type: 6 },
	{ name: '监督检查', type: 7 },
];

const tabOptions = computed(() =>
	tabList.map((tab, index) => ({
		text: tab.name,
		value: index,
	}))
);

const noticeType = computed(() => tabList[currentTab.value]?.type);

const paginationListRef = ref(null);

// SSR fetch：参数由 api/notice.js 统一构造
const { data: ssrListRes } = await useAsyncData(`notice-list-${pageType.value}-${pageStart.value}-${currentTab.value}`, async () => {
	try {
		return await getIndexAnnouncement(
			buildNoticeListParams({
				pageType: pageType.value,
				pageNumber: pageStart.value,
				pageSize,
				noticeType: tabList[currentTab.value]?.type,
				isGxzz: isGxzz.value,
			})
		);
	} catch {
		return { data: { list: [] } };
	}
});

function onDropdownChange(value) {
	currentTab.value = value;
	nextTick(() => {
		paginationListRef.value?.onRefresh();
	});
}

async function fetchNoticeList(page, pageSize) {
	// 首页使用 SSR 数据
	if (page === pageStart.value && import.meta.server) {
		return ssrListRes.value?.data?.list ?? [];
	}

	try {
		const res = await getIndexAnnouncement(
			buildNoticeListParams({
				pageType: pageType.value,
				pageNumber: page,
				pageSize,
				noticeType: noticeType.value,
				isGxzz: isGxzz.value,
			})
		);

		if (res.code == 200) {
			return res.data?.list || [];
		}
		return [];
	} catch {
		return [];
	}
}

// 详情地址统一（对齐 PC）：外链取 linkUrl，站内按子站点 /{companyId}/notice/{id}.html；
// 需登录且未登录时直接指向登录页，登录后回跳
function getItemRoute(item) {
	return buildNoticeItemHref(item, {
		loggedIn: isLoggedIn.value,
		isGxzz: isGxzz.value,
		isQuantity: pageType.value === 'quantity',
	});
}

// 与 PC 对齐：优先发布时间，缺失时回退创建时间
function getDisplayTime(item) {
	return item?.publishTime || item?.publishDate || item?.releaseTime || item?.pubTime || item?.createTime || '';
}

// 可点击区域一律渲染成 A 标签（源码可见），外链带 nofollow，站内走路由跳转
function getItemLinkAttrs(item) {
	if (!item.linkUrl) return { to: getItemRoute(item) };

	// 移动端不新开窗口
	return {
		href: item.linkUrl,
		...(isExternalLink(item.linkUrl) ? { rel: 'nofollow' } : {}),
	};
}

// 无封面 / 封面加载失败 -> 默认图；默认图也失败 -> 隐藏。
// 不能在 error 里直接改 e.target.src：兜底图 404 时会反复触发 error 导致闪烁，故用状态表兜底。
function getCoverKey(item) {
	return item?.coverUrl || COVER_PLACEHOLDER;
}

function isCoverHidden(item) {
	return coverStatus.get(getCoverKey(item)) === 'hidden';
}

function getCoverSrc(item) {
	return item?.coverUrl && coverStatus.get(item.coverUrl) !== 'fallback' ? item.coverUrl : COVER_PLACEHOLDER;
}

function handleImageError(e, item) {
	const key = getCoverKey(item);
	if (coverStatus.get(key) === 'fallback') {
		coverStatus.set(key, 'hidden');
		return;
	}
	coverStatus.set(key, 'fallback');
}
</script>

<style lang="scss" scoped>
.notice-list-container {
	min-height: 100vh;
	background: #f5f5f5;
}

.top-bar-container {
	position: sticky;
	top: 0;
	z-index: 1000;
	background: #fff;
}

.dropdown-container {
	border-bottom: 1px solid #ddd;
	position: relative;
	z-index: 999;
}

.dropdown-title {
	font-size: 14px;
	color: #333;
}

.notice-list {
	padding: 8px;
}

.info-box {
	background: #ffffff;
	margin-top: 8px;
	padding: 12px;
	display: flex;
	justify-content: space-between;
	cursor: pointer;
	text-decoration: none;
	color: inherit;

	&:first-child {
		margin-top: 0;
	}
}

/* 国械头条样式 */
.hot-info {
	width: 100%;
	display: flex;
	justify-content: space-between;
	gap: 12px;

	.hot-content {
		flex: 1;
		min-width: 0;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	}

	.hot-title {
		font-family: NotoSansSC-Medium, NotoSansSC;
		font-weight: 500;
		color: #1f1f1f;
		font-size: 14px;
		line-height: 22px;
		max-height: 44px;
		word-break: break-all;
		overflow: hidden;
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		line-clamp: 2;
	}

	.data-info {
		font-size: 1rem;
		font-family: NotoSansSC-Regular, NotoSansSC;
		font-weight: 400;
		color: #606266;
		line-height: 1.5rem;
		margin-top: 6px;
	}

	.right-img {
		width: 8.75rem;
		height: 5rem;
		flex-shrink: 0;

		.cover-img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			border-radius: 8px;
		}
	}
}

/* 质量公告样式 */
.quantity-info {
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;

	.title-wrap {
		flex: 1;
		min-width: 0;
		max-width: calc(100% - 120px);
		overflow: hidden;
	}

	.quantity-title {
		line-height: 20px;
		max-height: 40px;
		overflow: hidden;
		word-break: break-all;
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		line-clamp: 2;
	}

	.data-info {
		font-size: 1rem;
		font-family: NotoSansSC-Regular, NotoSansSC;
		font-weight: 400;
		color: #606266;
		line-height: 1.5rem;
		white-space: nowrap;
		margin-left: 12px;
	}
}

/* Vant 下拉菜单样式覆盖 */
:deep(.van-dropdown-menu) {
	z-index: 999 !important;
}

:deep(.van-dropdown-menu__title) {
	color: #333 !important;
}

:deep(.van-dropdown-menu__title--active) {
	color: #1989fa !important;
}

:deep(.van-dropdown-menu__item) {
	border-bottom: 1px solid #ddd;
}

:deep(.van-dropdown-menu__title::after) {
	border-bottom-color: #666 !important;
	border-left-color: #999 !important;
}
</style>
